import React, { useMemo } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { arrayOf, func, number, shape } from 'prop-types';
import MultipleSelectUser from '#component/form/multiple-select-user';
import { toMeta } from '#helper/rhf-meta';
import { fetchSelectUsers, clearSelectUsers } from '#action/user/user-select';
import DeviceField from './device-field/device-field';
import { uniq } from '#helper';

const customStyles = {
  input: {
    display: 'flex',
    padding: '6px 8px',
    minHeight: 54,
    height: 'auto'
  }
};

/**
 * Renders devices and users multiple select fields.
 * @returns {React.ReactFragment}
 */

const DevicesUsersSelectionFields = props => {
  const {
    formData,
    change,
    currentProject,
    initialDevices,
    disabled,
    forceError
  } = props;

  const dispatch = useDispatch();
  const { control, formState } = useFormContext();
  const allProjects = useSelector(s => s.account.directProjects, shallowEqual);
  const { users, status } = useSelector(s => s.userSelect, shallowEqual);

  const isRootProject = Boolean(allProjects.find(p =>
    p.level === 0 && p.id === Number(currentProject?.id)));

  const usersToSelect = useMemo(() => uniq(users, 'id').map(i => ({
    label: `${i.firstName} ${i.lastName}`,
    value: i.id,
    disabled: i.disabled,
    email: i.email
  })), [users]);

  const handleDeviceSetData = d => {
    change('devices', d);
  };

  const handleUsersFetch = e => dispatch(fetchSelectUsers(e.target.value));

  const clearUsers = e => {
    dispatch(clearSelectUsers());
    if (!e) {
      dispatch(fetchSelectUsers());
    }
  };

  const fetchUsers = () => {
    dispatch(fetchSelectUsers());
  };

  return (
    <>
      <DeviceField
        label="Devices"
        handleDeviceSetData={handleDeviceSetData}
        customStyles={customStyles}
        isRootProject={isRootProject}
        disabled={disabled || isRootProject}
        project={currentProject}
        openedFromCreateForm={true}
        formData={formData?.devices ?? []}
        initialDevices={initialDevices}
      />
      <Controller
        name="users"
        control={control}
        render={({ field, fieldState }) => (
          <MultipleSelectUser
            name={field.name}
            meta={toMeta(fieldState, formState)}
            label="Select users"
            placeholder="Select users"
            data={usersToSelect}
            setData={field.onChange}
            defaultData={formData.users}
            forceError={forceError}
            fetchUsers={handleUsersFetch}
            clearUsers={clearUsers}
            loading={['loading'].includes(status)}
            textFieldOnFocus={fetchUsers}
          />
        )}
      />
    </>
  );
};

DevicesUsersSelectionFields.propTypes = {
  devices: arrayOf(number),
  users: arrayOf(shape({})),
  formData: shape({}).isRequired,
  change: func.isRequired
};

export default DevicesUsersSelectionFields;
